import yaml from 'js-yaml';
import { GenesysYAML, FlowDefinition } from '../types';

const GEMINI_SYSTEM_PROMPT = `Convert Genesys Cloud YAML to Mermaid flowchart.

CRITICAL: Start with "graph TD" or "flowchart TD" FIRST, then classDef statements AFTER.

SYNTAX RULES:
1. NEVER use these as node IDs: end, start, graph, subgraph, class, click, call, style
2. Use unique IDs: flowEnd, flowStart, disconnect1, etc.
3. NO quotes in labels - remove all quote characters from label text
4. NO parentheses in labels - use brackets or remove them
5. NO special chars in labels: {, }, |, <, >, ", '
6. Apply classes with ::: syntax (e.g., nodeId:::className)

REQUIRED STRUCTURE (EXACT ORDER):
graph TD
classDef action fill:#e1f5fe,stroke:#0277bd
classDef decision fill:#fff9c4,stroke:#fbc02d
classDef terminator fill:#ffebee,stroke:#c62828

flowStart((Start)):::terminator
[... rest of nodes and connections ...]
flowEnd((End)):::terminator

Return ONLY valid Mermaid code, no markdown fences.`;

export class YAMLParser {
  static parse(yamlContent: string): GenesysYAML {
    return yaml.load(yamlContent) as GenesysYAML;
  }

  static async generateMermaidViaGemini(yamlContent: string, apiKey: string): Promise<string> {
    if (!apiKey) {
      throw new Error('Gemini API key is required');
    }

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: `Convert this Genesys Cloud YAML to a Mermaid flowchart:\n\n${yamlContent}`
            }]
          }],
          systemInstruction: {
            parts: [{ text: GEMINI_SYSTEM_PROMPT }]
          }
        })
      }
    );

    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error.message);
    }

    let mermaidCode = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
    
    // Clean up markdown fences if present
    mermaidCode = mermaidCode.replace(/```mermaid\n?/g, '').replace(/```\n?/g, '').trim();
    
    // Fix classDef order - ensure they come AFTER graph declaration
    const lines = mermaidCode.split('\n');
    const classDefLines: string[] = [];
    const otherLines: string[] = [];
    let graphLine = '';
    
    for (const line of lines) {
      const trimmed = line.trim();
      // Skip comment lines
      if (trimmed.startsWith('//')) {
        continue;
      }
      if (trimmed.startsWith('graph ') || trimmed.startsWith('flowchart ')) {
        graphLine = line;
      } else if (trimmed.startsWith('classDef ')) {
        classDefLines.push(line);
      } else {
        otherLines.push(line);
      }
    }
    
    // Reconstruct with correct order: graph, classDef, rest
    if (graphLine) {
      mermaidCode = [graphLine, ...classDefLines, ...otherLines].join('\n');
    }
    
    if (!mermaidCode || mermaidCode.length < 50) {
      throw new Error('Generated mermaid code is too short or empty');
    }

    return mermaidCode;
  }

  static async yamlToFlowDefinition(yamlContent: string, filename: string, apiKey: string): Promise<FlowDefinition> {
    const yamlData = this.parse(yamlContent);
    const { inboundCall } = yamlData;
    
    const countryMatch = filename.match(/^([A-Z]{2})/);
    const countryCode = countryMatch ? countryMatch[1] : 'XX';
    const region = this.getRegion(countryCode);
    
    const mermaidCode = await this.generateMermaidViaGemini(yamlContent, apiKey);
    
    return {
      id: filename.replace('.yaml', '').toLowerCase().replace(/\s+/g, '_'),
      title: inboundCall.name,
      meta: `Division: ${inboundCall.division} | Language: ${inboundCall.defaultLanguage}`,
      graph: mermaidCode,
      region,
      country: this.getCountryName(countryCode),
      type: 'main'
    };
  }

  private static getRegion(countryCode: string): 'APAC' | 'EMEA' | 'LATAM' {
    const apac = ['PH', 'TH', 'AU', 'NZ', 'SG', 'MY'];
    const latam = ['BR', 'CO', 'MX', 'AR', 'CL'];
    
    if (apac.includes(countryCode)) return 'APAC';
    if (latam.includes(countryCode)) return 'LATAM';
    return 'EMEA';
  }

  private static getCountryName(code: string): string {
    const countries: Record<string, string> = {
      'ZA': 'South Africa', 'UK': 'United Kingdom', 'SW': 'Sweden',
      'SK': 'Slovakia', 'RO': 'Romania', 'PL': 'Poland', 'NW': 'Norway',
      'NL': 'Netherlands', 'IT': 'Italy', 'HU': 'Hungary', 'FR': 'France',
      'ES': 'Spain', 'DK': 'Denmark', 'DE': 'Germany', 'CZ': 'Czech Republic',
      'CH': 'Switzerland', 'BR': 'Brazil', 'CO': 'Colombia', 'PH': 'Philippines'
    };
    return countries[code] || code;
  }
}
